import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Divider,
    Chip,
    Button,
} from '@material-ui/core';
import {
    AdditionalInfo, BackButton, BidPeriods, BidStatusChip,
} from '@commonClient2/components';
import { RESET_BID_PROFILE } from '../../store/bidProfile';
import { UPDATE_MANAGER_APP_DATA } from '../../store/appData';
import { RESET_BID_PROFILE_MANAGEMENT } from '../../store/bidProfileManagement';
import { completeDraft, UPDATE_DRAFT_BID_PROFILE, RESET_DRAFT_BID_PROFILE } from '../../store/bidProfileDraft';
import { RESET_EMPLOYEE_PREFERENCES_REPORT } from '../../../store/employeePreferencesReport';
import { RESET_UNAWARDED_SHIFTLINES_REPORT } from '../../../store/unawardedShiftlinesReport';
import { RESET_EMPLOYEE_BIDDING_RESULTS_REPORT } from '../../../store/employeeBiddingResultsReport';
import {
    BidProfileEditTab, BidProfileStatusNames, UkgDateFormat, BidProfileStatus
} from '../../../Enums';
import DateFormatService from '../../../services/DateFormatService';
import './BidEditHeader.scss';
import BidProfileSplitButton from './BidProfileSplitButton';

/** @param {string | Date | null | undefined} date */
const formatDate = (date) => (date ? DateFormatService.formatDateOrEmpty(date, UkgDateFormat.LongDateTime) : '');

/** @param {{ bidProfile: BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile | undefined }} param0 */
const BidEditHeader = ({ bidProfile }) => {
    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const { managerAppData } = useSelector((state) => state);
    const {
        hyperfinds,
        bidProfiles,
        bidProfileDrafts,
        managerBidSetups,
    } = managerAppData;

    const { t } = useTranslation('shiftBidding');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleReturnToList = () => {
        dispatch({ type: RESET_BID_PROFILE });
        dispatch({ type: RESET_DRAFT_BID_PROFILE });
        dispatch({ type: RESET_BID_PROFILE_MANAGEMENT });
        dispatch({ type: RESET_EMPLOYEE_PREFERENCES_REPORT });
        dispatch({ type: RESET_UNAWARDED_SHIFTLINES_REPORT });
        dispatch({ type: RESET_EMPLOYEE_BIDDING_RESULTS_REPORT });
        if (bidProfile !== undefined && bidProfile.status !== BidProfileStatus.Draft && bidProfile.isArchive) {
            navigate('/Manager/archive');
            dispatch({
                type: UPDATE_MANAGER_APP_DATA,
                payload: {
                    pageName: t('manager.common.archive'),
                }
            });
        } else {
            navigate('/Manager');
            dispatch({
                type: UPDATE_MANAGER_APP_DATA,
                payload: {
                    pageName: t('manager.common.manageBids'),
                },
            });
        }
    };

    const titleClassName = `manage-bids-header-bid-name${bidProfile?.originalBidProfileInformation.name ? '' : ' name-cell-no-name'}`;

    const submissionStartDate = bidProfile?.schedule?.submissionPeriodStartDate;
    const submissionEndDate = bidProfile?.schedule?.submissionPeriodEndDate;
    const isSubmissionDefined = submissionStartDate && submissionEndDate;
    const submission = `Submission Period: ${formatDate(submissionStartDate)} - ${formatDate(submissionEndDate)}`;
    const requestStartDate = bidProfile?.schedule?.requestPeriodStartDate;
    const requestEndDate = bidProfile?.schedule?.requestPeriodEndDate;
    const isRequestDefined = requestStartDate && requestEndDate;
    const isPeriodSectionVisible = isSubmissionDefined && isRequestDefined;
    const isDraft = !!bidProfile?.draftId;
    let isCompleted = false;

    if (isDraft) {
        isCompleted = bidProfile?.originalBidProfileInformation.isCompleted
            && bidProfile?.originalScheduleTemplates.isCompleted
            && bidProfile?.originalEmployees.isCompleted
            && bidProfile?.originalSchedule.isCompleted;
    }

    const isCompleteDraftActionDisabled = useRef(bidProfile?.isCompleteDraftActionProcessing);

    const handleComplete = () => {
        if (isCompleteDraftActionDisabled.current) {
            return;
        }
        dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: { isCompleteDraftActionProcessing: true } });
        isCompleteDraftActionDisabled.current = true;
        dispatch(
            completeDraft(
                bidProfile?.draftId,
                {
                    bidProfileDrafts,
                    bidProfiles,
                    hyperfinds,
                    managerBidSetups,
                },
            ),
        );
    };

    useEffect(() => {
        isCompleteDraftActionDisabled.current = bidProfile?.isCompleteDraftActionProcessing;
    }, [JSON.stringify(bidProfile)]);

    // @ts-ignore
    const statusName = BidProfileStatusNames[bidProfile?.status];

    return (
        <div className="bid-edit-header">
            <div className="bid-edit-sections">
                <div className="left-section">
                    <BackButton onClick={handleReturnToList} />
                    <div className="header-content">
                        <div className="first-line">
                            <h4 className={titleClassName}>
                                {bidProfile?.originalBidProfileInformation.name || t('manager.biddingList.defaultUnnamedDraftLabel')}
                            </h4>
                            <div className="manage-bids-header-status-container">
                                {
                                    bidProfile?.isRebid && (
                                        <Chip
                                            label={t('common.rebidNumber', { number: bidProfile.rebidNumber })}
                                            variant="outlined"
                                            className="manage-bids-rebid-chip"
                                        />
                                    )
                                }
                                <BidStatusChip statusName={statusName} />
                            </div>
                        </div>
                        {
                            isSubmissionDefined && <span className="submission-date">{submission}</span>
                        }
                    </div>
                </div>
                <div
                    className="right-section"
                    hidden={bidProfile?.tabSelected !== BidProfileEditTab.Dashboard}
                >
                    {isPeriodSectionVisible && (
                        <AdditionalInfo>
                            <BidPeriods
                                submissionPeriodStartDate={new Date(submissionStartDate)}
                                submissionPeriodEndDate={new Date(submissionEndDate)}
                                requestPeriodStartDate={new Date(requestStartDate)}
                                requestPeriodEndDate={new Date(requestEndDate)}
                            />
                        </AdditionalInfo>
                    )}
                    {bidProfile?.isManagementAllowed && (
                        <div className="buttons-container">
                            <BidProfileSplitButton
                                className={bidProfile?.tabSelected !== BidProfileEditTab.Dashboard ? 'hidden' : ''}
                            />
                        </div>
                    )}
                    {bidProfile?.isCreationAllowed && (
                        <div className="buttons-container">
                            <Button
                                color="primary"
                                variant="contained"
                                className={
                                    (!([BidProfileEditTab.Form, BidProfileEditTab.Summary]).includes(bidProfile?.tabSelected) 
                                        || !isDraft)
                                        ? 'hidden' 
                                        : ''
                                }
                                disabled={!isCompleted || isCompleteDraftActionDisabled.current}
                                style={{}}
                                onClick={() => handleComplete()}
                            >
                                {t('form.button.create')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            <Divider />
        </div>
    );
};

export default BidEditHeader;
